import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { Box, CircularProgress } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './contexts/AuthContext';
import { SnackbarProvider } from './contexts/SnackbarContext';
import { WikiTypesProvider } from './contexts/WikiTypesContext';
import { WikiItemProvider } from './contexts/WikiItemContext';
import { ProtectedRoute } from './components/auth';
import ScrollToTop from './components/ScrollToTop';
import CanonicalUrl from './components/CanonicalUrl';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import QaProtectedRoute from './components/QaProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { PersonalCabinetLayout, AdminLayout, QaLayout, PublicLayout } from './components/layout';
import HomeLayout from './components/layout/HomeLayout';
import WikiLayout from './components/layout/WikiLayout';
import AuthLayout from './components/layout/AuthLayout';
import RegisterLayout from './components/layout/RegisterLayout';
import WikiRestfulRouter from './pages/wiki/components/WikiRestfulRouter';
import { darkTheme } from './theme/darkTheme';
import { createLocalizedTheme, setDayjsLocale } from './utils/muiLocalization';
import MaintenancePage from './pages/MaintenancePage';
import { SurveyModal } from './components/home';
import { SURVEY } from './constants/config';
import './i18n/config';

// Lazy load public pages for code splitting
const HomePage = React.lazy(() => import('./pages/public/HomePage'));
const TestersPage = React.lazy(() => import('./pages/public/TestersPage'));
const DownloadsPage = React.lazy(() => import('./pages/public/DownloadsPage'));
const MapPage = React.lazy(() => import('./pages/public/MapPage'));
const TermsPage = React.lazy(() => import('./pages/public/TermsPage'));
const PrivacyPage = React.lazy(() => import('./pages/public/PrivacyPage'));
const RulesPage = React.lazy(() => import('./pages/public/RulesPage'));
const RaceBalancePage = React.lazy(() => import('./pages/public/RaceBalancePage'));
const PostsPage = React.lazy(() => import('./pages/public/PostsPage'));
const NotFoundPage = React.lazy(() => import('./pages/public/NotFoundPage'));

// Lazy load auth pages for code splitting
const LoginPage = React.lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/auth/RegisterPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/auth/ForgotPasswordPage'));
const PasswordResetPage = React.lazy(() => import('./pages/auth/PasswordResetPage'));
const L2PasswordResetPage = React.lazy(() => import('./pages/auth/L2PasswordResetPage'));
const EmailVerificationPage = React.lazy(() => import('./pages/auth/EmailVerificationPage'));
const GoogleCallbackPage = React.lazy(() => import('./pages/auth/GoogleCallbackPage'));
const YandexCallbackPage = React.lazy(() => import('./pages/auth/YandexCallbackPage'));
const VKCallbackPage = React.lazy(() => import('./pages/auth/VKCallbackPage'));

// =============================================
// MAINTENANCE MODE - set to true to show maintenance page
// =============================================
const MAINTENANCE_MODE = false;

// Lazy load admin pages for code splitting
const AdminIndexRedirect = React.lazy(() => import('./pages/admin/AdminIndexRedirect'));
const QaAccountsPage = React.lazy(() => import('./pages/qa/QaAccountsPage'));
const AdminSupportPage = React.lazy(() => import('./pages/admin/AdminSupportPage'));
const AdminSupportTicketDetailPage = React.lazy(() => import('./pages/admin/AdminSupportTicketDetailPage'));
const AdminShopPage = React.lazy(() => import('./pages/admin/AdminShopPage'));
const AdminRoulettePage = React.lazy(() => import('./pages/admin/AdminRoulettePage'));
const AdminReferralPage = React.lazy(() => import('./pages/admin/AdminReferralPage'));
const AdminPromoCodesPage = React.lazy(() => import('./pages/admin/AdminPromoCodesPage'));
const AdminUsersPage = React.lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminUserDetailPage = React.lazy(() => import('./pages/admin/AdminUserDetailPage'));
const AdminUserWarehousePage = React.lazy(() => import('./pages/admin/AdminUserWarehousePage'));
const AdminCharacterInventoryPage = React.lazy(() => import('./pages/admin/AdminCharacterInventoryPage'));
const AdminDonationBonusesPage = React.lazy(() => import('./pages/admin/AdminDonationBonusesPage'));
const AdminPaymentsPage = React.lazy(() => import('./pages/admin/AdminPaymentsPage'));
const AdminAnalyticsPage = React.lazy(() => import('./pages/admin/AdminAnalyticsPage'));
const AdminRouletteStatsPage = React.lazy(() => import('./pages/admin/AdminRouletteStatsPage'));
const AdminPromoStatsPage = React.lazy(() => import('./pages/admin/AdminPromoStatsPage'));
const AdminPollsPage = React.lazy(() => import('./pages/admin/AdminPollsPage'));
const AdminPostsPage = React.lazy(() => import('./pages/admin/AdminPostsPage'));
const AdminLauncherConfigsPage = React.lazy(() => import('./pages/admin/AdminLauncherConfigsPage'));
const AdminOrnamentsPage = React.lazy(() => import('./pages/admin/game-data/AdminOrnamentsPage'));
const AdminSynthesisPage = React.lazy(() => import('./pages/admin/game-data/AdminSynthesisPage'));
const AdminAlchemyPage = React.lazy(() => import('./pages/admin/game-data/AdminAlchemyPage'));

// Lazy load dashboard pages for code splitting
const MyAccountsPage = React.lazy(() => import('./pages/dashboard/MyAccountsPage'));
const MyCharactersPage = React.lazy(() => import('./pages/dashboard/MyCharactersPage'));
const CharacterInventoryPage = React.lazy(() => import('./pages/dashboard/CharacterInventoryPage'));
const MyWarehousePage = React.lazy(() => import('./pages/dashboard/MyWarehousePage'));
const ShopPage = React.lazy(() => import('./pages/dashboard/ShopPage'));
const RoulettePage = React.lazy(() => import('./pages/dashboard/RoulettePage'));
const ReferralPage = React.lazy(() => import('./pages/dashboard/ReferralPage'));
const RatingPage = React.lazy(() => import('./pages/dashboard/RatingPage'));
const PaymentPage = React.lazy(() => import('./pages/dashboard/PaymentPage'));
const SettingsPage = React.lazy(() => import('./pages/dashboard/SettingsPage'));
const PromoCodesPage = React.lazy(() => import('./pages/dashboard/PromoCodesPage'));
const CartPage = React.lazy(() => import('./pages/dashboard/CartPage'));
const PollsPage = React.lazy(() => import('./pages/dashboard/PollsPage'));
const GuidePage = React.lazy(() => import('./pages/public/guide'));
const SupportPage = React.lazy(() => import('./pages/support/SupportPage'));
const SupportTicketDetailPage = React.lazy(() => import('./pages/support/SupportTicketDetailPage'));

// Loading fallback component
const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress />
  </Box>
);

// Save tracking params to localStorage on any page
const SOURCE_STORAGE_KEY = 'source';
const REFERRAL_CODE_STORAGE_KEY = 'referralCode';
const DEVICE_CODE_STORAGE_KEY = 'deviceCode';

const TrackingParamsTracker: React.FC = () => {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Track source
    const source = searchParams.get('source');
    if (source) {
      localStorage.setItem(SOURCE_STORAGE_KEY, source);
    }

    // Track referral code
    const referralCode = searchParams.get('ref');
    if (referralCode) {
      localStorage.setItem(REFERRAL_CODE_STORAGE_KEY, referralCode);
    }

    // Track device code
    const deviceCode = searchParams.get('deviceCode');
    if (deviceCode) {
      localStorage.setItem(DEVICE_CODE_STORAGE_KEY, deviceCode);
    }
  }, [searchParams]);

  return null;
};

function App() {
  const { i18n } = useTranslation();

  // Create localized theme
  const localizedTheme = React.useMemo(() => {
    setDayjsLocale(i18n.language);
    return createLocalizedTheme(darkTheme, i18n.language);
  }, [i18n.language]);

  // Show maintenance page if enabled
  if (MAINTENANCE_MODE) {
    return (
      <ThemeProvider theme={localizedTheme}>
        <CssBaseline />
        <MaintenancePage />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={localizedTheme}>
      <CssBaseline />
      <ErrorBoundary>
      <Box sx={{ minHeight: '100dvh' }}>
        <SnackbarProvider>
        <AuthProvider>
        <Router>
          <ScrollToTop />
          <CanonicalUrl />
          <TrackingParamsTracker />
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes with Simple Layout (PublicHeader + AuthFooter only) */}
            <Route path="/" element={
              <HomeLayout showRatingBanner>
                <HomePage />
              </HomeLayout>
            } />
            {/* TestersPage отключена, файл сохранён для повторного включения в будущем. */}
            {/* <Route path="/testers" element={
              <HomeLayout>
                <TestersPage />
              </HomeLayout>
            } /> */}
            <Route path="/downloads" element={
              <HomeLayout>
                <DownloadsPage />
              </HomeLayout>
            } />
            <Route path="/map" element={
              <HomeLayout>
                <MapPage />
              </HomeLayout>
            } />
            <Route path="/race-balance" element={
              <HomeLayout>
                <RaceBalancePage />
              </HomeLayout>
            } />
            <Route path="/posts" element={
              <HomeLayout>
                <PostsPage />
              </HomeLayout>
            } />
            <Route path="/posts/:id" element={
              <HomeLayout>
                <PostsPage />
              </HomeLayout>
            } />
            <Route path="/terms" element={
              <PublicLayout>
                <TermsPage />
              </PublicLayout>
            } />
            <Route path="/privacy" element={
              <PublicLayout>
                <PrivacyPage />
              </PublicLayout>
            } />
            <Route path="/rules" element={
              <PublicLayout>
                <RulesPage />
              </PublicLayout>
            } />

            {/* Auth Routes with AuthLayout */}
            <Route path="/login" element={
              <AuthLayout>
                <LoginPage />
              </AuthLayout>
            } />
            <Route path="/register" element={
              <RegisterLayout>
                <RegisterPage />
              </RegisterLayout>
            } />
            <Route path="/forgot-password" element={
              <AuthLayout>
                <ForgotPasswordPage />
              </AuthLayout>
            } />
            <Route path="/password/:token" element={
              <AuthLayout>
                <PasswordResetPage />
              </AuthLayout>
            } />
            <Route path="/l2/password/:token" element={
              <AuthLayout>
                <L2PasswordResetPage />
              </AuthLayout>
            } />
            <Route path="/auth/:token" element={
              <AuthLayout>
                <EmailVerificationPage />
              </AuthLayout>
            } />
            <Route path="/auth/google/callback" element={
              <AuthLayout>
                <GoogleCallbackPage />
              </AuthLayout>
            } />
            <Route path="/auth/yandex/callback" element={
              <AuthLayout>
                <YandexCallbackPage />
              </AuthLayout>
            } />
            <Route path="/auth/vk/callback" element={
              <AuthLayout>
                <VKCallbackPage />
              </AuthLayout>
            } />

            {/* Wiki Routes with RESTful Router */}
            <Route path="/wiki/*" element={
              <WikiTypesProvider>
                <WikiItemProvider>
                  <WikiLayout>
                    <WikiRestfulRouter />
                  </WikiLayout>
                </WikiItemProvider>
              </WikiTypesProvider>
            } />

            {/* Admin Routes - Lazy loaded for code splitting */}
            <Route
              path="/admin/*"
              element={
                <AdminProtectedRoute>
                  <AdminLayout>
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        <Route path="/" element={<AdminIndexRedirect />} />
                        <Route path="support" element={<AdminSupportPage />} />
                        <Route path="support/:ticketId" element={<AdminSupportTicketDetailPage />} />
                        <Route path="shop" element={<AdminShopPage />} />
                        <Route path="roulette" element={<AdminRoulettePage />} />
                        <Route path="referrals" element={<AdminReferralPage />} />
                        <Route path="promo-codes" element={<AdminPromoCodesPage />} />
                        <Route path="users" element={<AdminUsersPage />} />
                        <Route path="users/:userId" element={<AdminUserDetailPage />} />
                        <Route path="users/:userId/warehouse" element={<AdminUserWarehousePage />} />
                        <Route path="users/:userId/characters/:charId/inventory" element={<AdminCharacterInventoryPage />} />
                        <Route path="donation-bonuses" element={<AdminDonationBonusesPage />} />
                        <Route path="roulette-stats" element={<AdminRouletteStatsPage />} />
                        <Route path="roulette-stats/users/:userId" element={<AdminRouletteStatsPage />} />
                        <Route path="promo-stats" element={<AdminPromoStatsPage />} />
                        <Route path="promo-stats/users/:userId" element={<AdminPromoStatsPage />} />
                        <Route path="payments" element={<AdminPaymentsPage />} />
                        <Route path="payments/clans/:clanId" element={<AdminPaymentsPage />} />
                        <Route path="payments/shop/:shopItemId" element={<AdminPaymentsPage />} />
                        <Route path="analytics" element={<AdminAnalyticsPage />} />
                        <Route path="polls" element={<AdminPollsPage />} />
                        <Route path="posts" element={<AdminPostsPage />} />
                        <Route path="launcher-configs" element={<AdminLauncherConfigsPage />} />
                        <Route path="game-data/ornaments" element={<AdminOrnamentsPage />} />
                        <Route path="game-data/synthesis" element={<AdminSynthesisPage />} />
                        <Route path="game-data/alchemy" element={<AdminAlchemyPage />} />
                        <Route path="*" element={<Navigate to="/admin" replace />} />
                      </Routes>
                    </Suspense>
                  </AdminLayout>
                </AdminProtectedRoute>
              }
            />

            {/* QA Routes — отдельная зона (как админка, но для QA/ADMIN/OWNER) */}
            <Route
              path="/qa/*"
              element={
                <QaProtectedRoute>
                  <QaLayout>
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        <Route path="/" element={<Navigate to="/qa/accounts" replace />} />
                        <Route path="accounts" element={<QaAccountsPage />} />
                        <Route path="*" element={<Navigate to="/qa/accounts" replace />} />
                      </Routes>
                    </Suspense>
                  </QaLayout>
                </QaProtectedRoute>
              }
            />

            {/* User Dashboard Routes - Lazy loaded for code splitting */}
            <Route
              path="/dashboard/*"
              element={
                <ProtectedRoute>
                  <PersonalCabinetLayout>
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        <Route path="accounts" element={<MyAccountsPage />} />
                        <Route path="characters" element={<MyCharactersPage />} />
                        <Route path="characters/:charName/inventory" element={<CharacterInventoryPage />} />
                        <Route path="warehouse" element={<MyWarehousePage />} />
                        <Route path="shop" element={<ShopPage />} />
                        <Route path="cart" element={<CartPage />} />
                        <Route path="item-wheel" element={<RoulettePage />} />
                        <Route path="referrals" element={<ReferralPage />} />
                        <Route path="ratings" element={<RatingPage />} />
                        <Route path="support" element={<SupportPage />} />
                        <Route path="support/:ticketId" element={<SupportTicketDetailPage />} />
                        <Route path="promo-codes" element={<PromoCodesPage />} />
                        <Route path="payment" element={<PaymentPage />} />
                        <Route path="polls" element={<PollsPage />} />
                        <Route path="settings" element={<SettingsPage />} />
                        <Route path="*" element={<Navigate to="/dashboard/accounts" replace />} />
                      </Routes>
                    </Suspense>
                  </PersonalCabinetLayout>
                </ProtectedRoute>
              }
            />

            {/* Guide — маршрут публичный, но содержимое зависит от логина: гость видит только
                выпущенные секции (PUBLIC_GUIDE_SECTIONS в constants/config), залогиненный — весь
                гайд. Фильтрует сама GuidePage, охраны здесь намеренно нет. */}
            <Route path="/guide" element={
              <HomeLayout>
                <GuidePage />
              </HomeLayout>
            } />

            {/* 404 Not Found - Must be last */}
            <Route path="*" element={
              <HomeLayout>
                <NotFoundPage />
              </HomeLayout>
            } />
          </Routes>
          </Suspense>
          {SURVEY.ENABLED && <SurveyModal />}
        </Router>
        </AuthProvider>
        </SnackbarProvider>
      </Box>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
